import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  FileButton,
  Group,
  MultiSelect,
  ScrollArea,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  Stepper,
  Switch,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconCloudUpload } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

function ContactsImport() {
  const [step, setStep] = useState(0);
  const [csv, setCsv] = useState<string>('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [columnMap, setColumnMap] = useState<{
    waId?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    languageCode?: string;
    countryId?: string;
    customFields?: Record<string, number>;
  }>({});
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [mergeMode, setMergeMode] = useState<'skip' | 'merge'>('skip');
  const [applySuppression, setApplySuppression] = useState(true);

  const groups = trpc.contacts.groups.list.useQuery();
  const labels = trpc.chat.allLabels.useQuery();
  const customFields = trpc.contacts.customFields.list.useQuery();

  function resetWizard() {
    setStep(0);
    setCsv('');
    setHeaders([]);
    setColumnMap({});
    setGroupIds([]);
    setLabelIds([]);
    setMergeMode('skip');
    setApplySuppression(true);
  }

  const preview = trpc.contacts.import.preview.useMutation({
    onSuccess: () => setStep(2),
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const commit = trpc.contacts.import.commit.useMutation({
    onSuccess: (r) => {
      notifications.show({
        message: `Imported ${r.created} contacts (${r.skipped} skipped)`,
        color: 'green',
      });
      setStep(3);
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  async function handleFile(f: File | null) {
    if (!f) return;
    const text = await f.text();
    setCsv(text);
    const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
    setHeaders(firstLine.split(',').map((s) => s.trim()));
    setStep(1);
  }

  function buildPayload() {
    return {
      csv,
      columnMap: { waId: columnMap.waId!, ...columnMap },
      groupIds: groupIds.map(Number),
      labelIds: labelIds.map(Number),
      applySuppression,
      mergeMode,
    };
  }

  function runPreview() {
    if (!columnMap.waId) {
      notifications.show({ message: 'Map the WhatsApp ID column', color: 'red' });
      return;
    }
    preview.mutate(buildPayload());
  }

  function runCommit() {
    if (!columnMap.waId) return;
    commit.mutate(buildPayload());
  }

  const cfHeaders = (customFields.data ?? []).filter(
    (f) =>
      columnMap.customFields?.[f.inputName] !== undefined ||
      headers.includes(`cf_${f.inputName}`) ||
      headers.includes(f.inputName),
  );

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Title order={2}>Import contacts</Title>
        <Stepper active={step} onStepClick={setStep} allowNextStepsSelect={false}>
          <Stepper.Step label="Upload" description="CSV file">
            <Card mt="md">
              <Stack align="center" gap="md">
                <FileButton onChange={handleFile} accept=".csv,text/csv">
                  {(props) => (
                    <Button {...props} leftSection={<IconCloudUpload size={16} />}>
                      Choose CSV file
                    </Button>
                  )}
                </FileButton>
                <Text c="dimmed" size="sm">
                  First row must contain column headers. Required: a column with WhatsApp IDs (any
                  format — we strip non-digits).
                </Text>
              </Stack>
            </Card>
          </Stepper.Step>

          <Stepper.Step label="Map" description="Column → field">
            <Card mt="md">
              <Stack gap="sm">
                <Group grow>
                  <Select
                    required
                    label="WhatsApp ID column"
                    data={headers}
                    value={columnMap.waId ?? null}
                    onChange={(v) => setColumnMap({ ...columnMap, waId: v ?? undefined })}
                  />
                  <Select
                    label="First name column"
                    data={headers}
                    clearable
                    value={columnMap.firstName ?? null}
                    onChange={(v) => setColumnMap({ ...columnMap, firstName: v ?? undefined })}
                  />
                </Group>
                <Group grow>
                  <Select
                    label="Last name column"
                    data={headers}
                    clearable
                    value={columnMap.lastName ?? null}
                    onChange={(v) => setColumnMap({ ...columnMap, lastName: v ?? undefined })}
                  />
                  <Select
                    label="Email column"
                    data={headers}
                    clearable
                    value={columnMap.email ?? null}
                    onChange={(v) => setColumnMap({ ...columnMap, email: v ?? undefined })}
                  />
                </Group>
                <Group grow>
                  <Select
                    label="Language column"
                    data={headers}
                    clearable
                    value={columnMap.languageCode ?? null}
                    onChange={(v) => setColumnMap({ ...columnMap, languageCode: v ?? undefined })}
                  />
                  <Select
                    label="Country column"
                    data={headers}
                    clearable
                    value={columnMap.countryId ?? null}
                    onChange={(v) => setColumnMap({ ...columnMap, countryId: v ?? undefined })}
                  />
                </Group>

                {customFields.isPending ? (
                  <Skeleton height={48} />
                ) : (customFields.data ?? []).length > 0 ? (
                  <Stack gap={4}>
                    <Text size="sm" fw={500}>
                      Custom field mapping
                    </Text>
                    {(customFields.data ?? []).map((f) => (
                      <Select
                        key={f.id}
                        label={f.inputName}
                        description={`type: ${f.inputType}`}
                        data={headers}
                        clearable
                        value={
                          Object.entries(columnMap.customFields ?? {}).find(
                            ([, fid]) => fid === f.id,
                          )?.[0] ?? null
                        }
                        onChange={(csvCol) => {
                          const next = { ...(columnMap.customFields ?? {}) };
                          // Drop any previous mapping pointing to this field id.
                          for (const [k, v] of Object.entries(next)) {
                            if (v === f.id) delete next[k];
                          }
                          if (csvCol) next[csvCol] = f.id;
                          setColumnMap({ ...columnMap, customFields: next });
                        }}
                      />
                    ))}
                  </Stack>
                ) : null}

                <SegmentedControl
                  value={mergeMode}
                  onChange={(v) => setMergeMode(v as 'skip' | 'merge')}
                  data={[
                    { value: 'skip', label: 'Skip duplicates' },
                    { value: 'merge', label: 'Merge into existing' },
                  ]}
                />
                {mergeMode === 'merge' && (
                  <Alert color="yellow">
                    Merge mode updates non-empty columns from the CSV onto existing contacts. Custom
                    field values are upserted. Bot toggles + opt-out flags are preserved.
                  </Alert>
                )}

                {groups.isPending ? (
                  <Skeleton height={36} />
                ) : (
                  <MultiSelect
                    label="Add to groups"
                    description="Long-lived contact lists. Pick existing groups to file imported contacts into."
                    data={(groups.data ?? []).map((g) => ({
                      value: String(g.id),
                      label: g.title,
                    }))}
                    value={groupIds}
                    onChange={setGroupIds}
                  />
                )}
                {labels.isPending ? (
                  <Skeleton height={36} />
                ) : (
                  <MultiSelect
                    label="Apply labels"
                    description="Cohort tags (e.g. 'Spring 2026 webinar'). Useful for targeting a campaign at the imported batch."
                    data={(labels.data ?? []).map((l) => ({
                      value: String(l.id),
                      label: l.title,
                    }))}
                    value={labelIds}
                    onChange={setLabelIds}
                  />
                )}
                <Switch
                  label="Skip contacts on the suppression list"
                  description="Rows whose phone number is on your do-not-contact list are rejected with a clear reason instead of being imported."
                  checked={applySuppression}
                  onChange={(e) => setApplySuppression(e.currentTarget.checked)}
                />
                <Group>
                  <Button onClick={runPreview} loading={preview.isPending}>
                    Preview
                  </Button>
                </Group>
              </Stack>
            </Card>
          </Stepper.Step>

          <Stepper.Step label="Preview" description="Dry-run">
            <Card mt="md">
              {preview.data ? (
                <Stack gap="sm">
                  <Group>
                    <Badge color="green" variant="light">
                      {preview.data.validRows} valid
                    </Badge>
                    <Badge color="yellow" variant="light">
                      {preview.data.duplicates} duplicates
                    </Badge>
                    <Badge color="red" variant="light">
                      {preview.data.invalid} invalid
                    </Badge>
                  </Group>
                  {preview.data.duplicates > 0 && mergeMode === 'skip' && (
                    <Alert color="yellow" icon={<IconAlertCircle />}>
                      Duplicates already exist in your contact list and will be skipped on import.
                      Switch to <em>Merge into existing</em> to update them.
                    </Alert>
                  )}
                  {preview.data.errors.length > 0 && (
                    <Card>
                      <Text fw={500} size="sm" mb="xs">
                        Sample errors ({preview.data.errors.length})
                      </Text>
                      <ScrollArea h={180}>
                        <Table withRowBorders={false} striped>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Row</Table.Th>
                              <Table.Th>Reason</Table.Th>
                              <Table.Th>Raw</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {preview.data.errors.map((e, i) => (
                              <Table.Tr key={i}>
                                <Table.Td>
                                  <Text ff="monospace" size="xs">
                                    {e.rowIndex}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="xs" c="red">
                                    {e.reason}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Code block style={{ fontSize: 11 }}>
                                    {Object.entries(e.raw)
                                      .map(([k, v]) => `${k}: ${v}`)
                                      .slice(0, 3)
                                      .join(' · ')}
                                  </Code>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                    </Card>
                  )}
                  <Table striped withRowBorders={false}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>WhatsApp ID</Table.Th>
                        <Table.Th>Email</Table.Th>
                        {cfHeaders.slice(0, 3).map((f) => (
                          <Table.Th key={f.id}>{f.inputName}</Table.Th>
                        ))}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {preview.data.sample.map((row, idx) => (
                        <Table.Tr key={idx}>
                          <Table.Td>
                            {[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}
                          </Table.Td>
                          <Table.Td>
                            <Text ff="monospace" size="sm">
                              {row.waIdCanonical ?? `(invalid: ${row.waIdRaw ?? '—'})`}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              {row.email ?? '—'}
                            </Text>
                          </Table.Td>
                          {cfHeaders.slice(0, 3).map((f) => (
                            <Table.Td key={f.id}>
                              <Text size="xs">{row.customValues[f.id] ?? ''}</Text>
                            </Table.Td>
                          ))}
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                  <Group>
                    <Button
                      onClick={runCommit}
                      loading={commit.isPending}
                      leftSection={<IconCheck size={14} />}
                    >
                      {`Import ${preview.data.validRows - (mergeMode === 'skip' ? preview.data.duplicates : 0)} contacts`}
                    </Button>
                  </Group>
                </Stack>
              ) : (
                <Text c="dimmed">Run preview first.</Text>
              )}
            </Card>
          </Stepper.Step>

          <Stepper.Completed>
            <Card mt="md">
              <Stack align="center" gap="sm">
                <IconCheck color="green" size={48} />
                <Text fw={600}>Import complete</Text>
                {commit.data && (
                  <Text c="dimmed" size="sm">
                    Created {commit.data.created} new contacts; skipped {commit.data.skipped}{' '}
                    duplicates.
                  </Text>
                )}
                <Group>
                  <Button onClick={resetWizard} variant="default">
                    Import another CSV
                  </Button>
                  <Button component={Link} to="/vendor/contacts" variant="default">
                    Back to contacts
                  </Button>
                </Group>
              </Stack>
            </Card>
          </Stepper.Completed>
        </Stepper>
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/contacts/import')({ component: ContactsImport });
